import { lazy, Suspense } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { authPaths } from "./routes";
import { AuthLayout } from "../auth/AuthLayout";

import { authRouteEntries } from "../auth/routes";

const Application = lazy(() =>
  import("../application/Application").then((module) => ({
    default: module.Application,
  })),
);

export function ApplicationRoutes() {
  return (
    <Routes>
      <Route element={<SessionLayout />}>
        {authRouteEntries.map((route) => (
          <Route key={route.path} {...route} />
        ))}
        <Route path="*" element={<Application />} />
      </Route>
    </Routes>
  );
}

function SessionLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthRoute = Object.values(authPaths).some(
    (path) => path === location.pathname.replace(/\/+$/, ""),
  );
  if (auth.status === "loading") return <SessionLoading />;
  if (auth.status === "unavailable")
    return (
      <AuthLayout mode="sign-in" onBack={() => navigate(-1)}>
        <h2>Authentication is temporarily unavailable.</h2>
        <p>Your session could not be checked. Please try again.</p>
        <button onClick={() => void auth.refreshSession()} type="button">
          Try again
        </button>
      </AuthLayout>
    );
  if (auth.status === "signed-out" && !isAuthRoute) {
    return (
      <Navigate
        to={{ pathname: authPaths["sign-in"], search: location.search }}
        replace
        state={{ returnTo: location.pathname + location.search }}
      />
    );
  }
  if ((auth.status === "signed-in" || auth.status === "guest") && isAuthRoute) {
    const requested = location.state?.returnTo;
    const target =
      typeof requested === "string" &&
      requested.startsWith("/") &&
      !requested.startsWith("//") &&
      !requested.startsWith("/auth/")
        ? requested
        : `/discover${invitationSearch(location.search)}`;
    return <Navigate to={target} replace />;
  }
  return (
    <Suspense fallback={<SessionLoading />}>
      <Outlet />
    </Suspense>
  );
}

export function SessionLoading() {
  const navigate = useNavigate();
  return (
    <AuthLayout mode="sign-in" onBack={() => navigate(-1)}>
      <p role="status">Loading your session…</p>
    </AuthLayout>
  );
}

function invitationSearch(search: string): string {
  const invite = new URLSearchParams(search).get("invite");
  return invite ? `?${new URLSearchParams({ invite })}` : "";
}
